import React, { useEffect, useState, useRef } from 'react'
import {
  buildIframeSrc,
  initialPortalVersion,
  portalToggleClass,
  syncPortalQueryParam,
  type PortalVersion,
} from './portalVersion'

declare global {
  interface Window {
    impersonation_ephemeralToken: string | undefined
    impersonation_tokenExpirationSeconds: number | undefined
    impersonation_email: string | undefined
    impersonation_tenantAccountCuid: string | undefined
  }
}

let billingPortalOrigin = 'https://localhost:3003'
window.location.hostname === 'dev.admin.kingenergy.com' && (billingPortalOrigin = 'https://dev.service.kingenergy.com')
window.location.hostname === 'admin.kingenergy.com' && (billingPortalOrigin = 'https://service.kingenergy.com')

export const Impersonate: React.FC = () => {
  const [timeRemaining, _setTimeRemaining] = useState(window.impersonation_tokenExpirationSeconds)
  const [portalVersion, setPortalVersion] = useState<PortalVersion>(initialPortalVersion)

  const timeRemainingRef = useRef(timeRemaining)
  const setTimeRemaining = (timeRemaining: any): void => {
    timeRemainingRef.current = timeRemaining
    _setTimeRemaining(timeRemaining)
  }

  const [iframeFailed, setIframeFailed] = useState(false)

  const selectPortalVersion = (version: PortalVersion): void => {
    setPortalVersion(version)
    syncPortalQueryParam(version)
    setIframeFailed(false)
  }

  const iframeSrc = buildIframeSrc(portalVersion, billingPortalOrigin)

  const handleIframeLoad = (): void => {
    const iframe = document.getElementById('iframe') as HTMLIFrameElement
    // targetOrigin stays the portal origin (path prefix, same origin for /v2)
    iframe.contentWindow?.postMessage(
      {
        email: window.impersonation_email,
        atk: window.impersonation_ephemeralToken,
        tenantAccountCuid: window.impersonation_tenantAccountCuid,
      },
      billingPortalOrigin
    )
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(Number(timeRemainingRef.current) - 1)
      if (Number(timeRemainingRef.current) < 0) {
        clearInterval(interval)
      }
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [])

  if (window.impersonation_ephemeralToken == null || Number(timeRemainingRef.current) <= 0) {
    return (
      <div className="p-4">
        Impersonation session invalidated.
        <button
          type="button"
          onClick={() => {
            window.close()
          }}
          className="ml-3 inline-flex items-center px-2.5 py-1.5 border border-transparent text-sm font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Exit
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen">
      <div className="w-full bg-red-500 text-white p-2 text-center font-normal">
        Viewing account <strong>{window.impersonation_email}</strong> in read-only mode.
        <div className="w-16 inline-block text-center">
          ({Math.floor(Number(timeRemaining) / 60)}:{Number(timeRemaining) % 60 < 10 ? 0 : ''}
          {Number(timeRemaining) % 60})
        </div>
        <span className="ml-3 inline-flex items-center gap-1 text-sm">
          Portal:
          <button
            type="button"
            onClick={() => selectPortalVersion('v1')}
            className={`px-2 py-0.5 rounded border text-xs font-medium ${portalToggleClass(portalVersion === 'v1')}`}
          >
            v1
          </button>
          <button
            type="button"
            onClick={() => selectPortalVersion('v2')}
            className={`px-2 py-0.5 rounded border text-xs font-medium ${portalToggleClass(portalVersion === 'v2')}`}
          >
            v2
          </button>
        </span>
        <button
          type="button"
          onClick={() => {
            window.close()
          }}
          className="ml-1 inline-flex items-center px-2.5 py-0.5 border shadow-sm text-sm font-medium rounded text-red-500 bg-white hover:bg-red-50 focus:outline-none"
        >
          Exit
        </button>
      </div>
      {iframeFailed && (
        <div className="bg-amber-100 text-amber-900 text-sm text-center p-2">
          Portal failed to load. Try the other version or check that the customer
          portal is running.
        </div>
      )}
      <iframe
        key={iframeSrc}
        src={iframeSrc}
        id="iframe"
        frameBorder="0"
        onLoad={handleIframeLoad}
        onError={() => setIframeFailed(true)}
        className="w-full flex-1"
      ></iframe>
    </div>
  )
}

export default Impersonate
